import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { addPrintToCart } from "../actions/cart";
import { API_BASE } from "../helpers/api";
import { savePrintPreview } from "../helpers/printPreviews";

const MAX_BYTES = 25 * 1024 * 1024;
const DEFAULT_PREVIEW_COLOR = "#64748b";

const normalizeHexColor = (value) => {
  const trimmed = String(value || "").trim();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(withHash)
    ? withHash
    : DEFAULT_PREVIEW_COLOR;
};

export default function ThreeDPrint() {
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const meshRef = useRef(null);
  const controlsRef = useRef(null);

  const [csrfToken, setCsrfToken] = useState("");
  const [colors, setColors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [quote, setQuote] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [error, setError] = useState("");
  const [cartMessage, setCartMessage] = useState("");
  const [form, setForm] = useState({
    material: "pla",
    quality: "standard",
    infill_percent: "20",
    quantity: "1",
    color_id: "",
    design_hours: "0",
    finishing_hours: "0",
    express: false,
    email: "",
    notes: "",
  });

  useEffect(() => {
    fetch(`${API_BASE}/csrf/`, { credentials: "include" })
      .then((response) => response.json())
      .then((data) => setCsrfToken(data.csrfToken))
      .catch(() => {});
    fetch(`${API_BASE}/prints/options/`, { credentials: "include" })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) => {
        setColors(Array.isArray(data.colors) ? data.colors : []);
        setMaterials(Array.isArray(data.materials) ? data.materials : []);
        setQualities(Array.isArray(data.qualities) ? data.qualities : []);
      })
      .catch(() => {});
  }, []);

  useEffect(
    () => () => {
      if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const selectedColor = useMemo(
    () => colors.find((color) => String(color.id) === String(form.color_id)) || null,
    [colors, form.color_id]
  );
  const previewColor = normalizeHexColor(selectedColor?.hex_code);

  useEffect(() => {
    if (!meshRef.current?.material) return;
    meshRef.current.material.color.set(previewColor);
    meshRef.current.material.needsUpdate = true;
  }, [previewColor]);

  useEffect(() => {
    if (!previewUrl || !canvasRef.current) return undefined;
    let mounted = true;
    let animationId;
    const canvas = canvasRef.current;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    rendererRef.current = renderer;
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 1000);
    cameraRef.current = camera;
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.enablePan = false;
    controlsRef.current = controls;

    scene.add(new THREE.HemisphereLight(0xe0f2fe, 0x0f172a, 1.35));
    const key = new THREE.DirectionalLight(0xffffff, 1.8);
    key.position.set(4, 6, 5);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x38bdf8, 1.1);
    rim.position.set(-5, 2, -4);
    scene.add(rim);
    const grid = new THREE.GridHelper(10, 20, 0x334155, 0x1e293b);
    grid.position.y = -1.6;
    scene.add(grid);

    const resize = () => {
      const width = canvas.clientWidth || 600;
      const height = canvas.clientHeight || 420;
      renderer.setSize(width, height, false);
      camera.aspect = width / Math.max(height, 1);
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", resize);
    resize();

    new STLLoader().load(
      previewUrl,
      (geometry) => {
        if (!mounted) return geometry.dispose();
        geometry.computeVertexNormals();
        geometry.computeBoundingBox();
        geometry.center();
        const material = new THREE.MeshStandardMaterial({
          color: previewColor,
          metalness: 0.08,
          roughness: 0.46,
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.rotation.x = -Math.PI / 2;
        meshRef.current = mesh;
        scene.add(mesh);
        geometry.computeBoundingSphere();
        const radius = geometry.boundingSphere?.radius || 1;
        const distance = Math.max(radius * 3.2, 3);
        camera.position.set(distance, distance * 0.8, distance);
        camera.near = Math.max(distance / 100, 0.01);
        camera.far = distance * 30;
        camera.updateProjectionMatrix();
        controls.target.set(0, 0, 0);
        controls.minDistance = Math.max(radius * 0.7, 1);
        controls.maxDistance = distance * 5;
        controls.update();
      },
      undefined,
      () => mounted && setError("The STL could not be previewed.")
    );

    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      animationId = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      mounted = false;
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationId);
      controls.dispose();
      if (meshRef.current) {
        meshRef.current.geometry.dispose();
        meshRef.current.material.dispose();
      }
      renderer.dispose();
      meshRef.current = null;
    };
  }, [previewUrl, previewColor]);

  const update = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setQuote(null);
    setCartMessage("");
  };

  const chooseFile = (nextFile) => {
    setError("");
    setQuote(null);
    setCartMessage("");
    if (!nextFile) return;
    if (!nextFile.name.toLowerCase().endsWith(".stl")) {
      setError("Upload an STL file.");
      return;
    }
    if (nextFile.size > MAX_BYTES) {
      setError("File is larger than the 25MB limit.");
      return;
    }
    setFile(nextFile);
    setPreviewUrl(URL.createObjectURL(nextFile));
  };

  const getQuote = async () => {
    if (!file || !csrfToken) return;
    setUploading(true);
    setError("");
    setQuote(null);
    try {
      const body = new FormData();
      body.append("file", file);
      Object.entries(form).forEach(([key, value]) => body.append(key, String(value)));
      const response = await fetch(`${API_BASE}/prints/upload/`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Unable to prepare this quote.");
      setQuote(data);
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const addToCart = async () => {
    if (!quote?.id) return;
    setAddingToCart(true);
    setError("");
    const success = await dispatch(
      addPrintToCart({ printRequestId: quote.id, notes: form.notes })
    );
    if (success) {
      try {
        savePrintPreview(quote.id, canvasRef.current.toDataURL("image/png", 0.9));
      } catch (err) {
        // Preview snapshots are optional.
      }
      setCartMessage("3D printing quote added to your cart.");
    } else {
      setError("Could not add this quote to your cart.");
    }
    setAddingToCart(false);
  };

  const fieldClass =
    "mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 dark:border-slate-600 dark:bg-slate-900 dark:text-white";
  const materialList = materials.length
    ? materials
    : [{ id: "pla", name: "PLA", description: "General-purpose material" }];
  const qualityList = qualities.length
    ? qualities
    : [{ id: "standard", name: "Standard · 0.20 mm" }];

  return (
    <div className="overflow-hidden rounded-3xl bg-slate-950 text-white shadow-2xl">
      <section className="relative border-b border-white/10 px-6 py-14 md:px-12">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(circle at 82% 24%, #0891b2 0, transparent 27%), linear-gradient(120deg, transparent 52%, #1e293b 100%)",
          }}
        />
        <div className="relative max-w-3xl">
          <p className="mb-3 text-sm font-bold uppercase tracking-[0.24em] text-cyan-400">
            Custom FDM printing
          </p>
          <h1 className="text-4xl font-black leading-tight md:text-6xl">
            From digital model to functional part.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            Upload your STL, inspect it in 3D, choose the material and finish,
            then receive an instant Australian-dollar estimate.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 text-sm font-semibold text-slate-200">
            {["One-offs welcome", "Functional prototypes", "Replacement parts", "Fast local turnaround"].map(
              (label) => (
                <span key={label} className="rounded-full border border-white/15 bg-white/5 px-4 py-2">
                  {label}
                </span>
              )
            )}
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-[0.95fr_1.05fr]">
        <div className="bg-slate-900 p-6 md:p-10">
          <h2 className="text-2xl font-bold">1. Upload and inspect</h2>
          <div
            onDrop={(event) => {
              event.preventDefault();
              chooseFile(event.dataTransfer.files?.[0]);
            }}
            onDragOver={(event) => event.preventDefault()}
            className="mt-5 rounded-2xl border-2 border-dashed border-slate-600 bg-slate-950/60 p-7 text-center transition hover:border-cyan-500"
          >
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-cyan-500/15 text-2xl text-cyan-400">
              ↑
            </div>
            <p className="font-semibold">Drop your STL here</p>
            <p className="mt-1 text-sm text-slate-400">Secure upload · maximum 25MB</p>
            <label className="mt-5 inline-flex cursor-pointer rounded-lg bg-cyan-400 px-5 py-2.5 font-black text-slate-950 hover:bg-cyan-300">
              Choose file
              <input
                type="file"
                accept=".stl"
                className="sr-only"
                onChange={(event) => chooseFile(event.target.files?.[0])}
              />
            </label>
          </div>

          <div className="mt-5 overflow-hidden rounded-2xl border border-slate-700 bg-slate-950">
            <div className="relative h-[390px]">
              <canvas ref={canvasRef} className="h-full w-full" />
              {!file && (
                <div className="absolute inset-0 flex items-center justify-center text-center text-slate-500">
                  <div>
                    <div className="text-5xl">◇</div>
                    <p className="mt-3 font-semibold">Your interactive model preview</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-slate-800 px-4 py-3 text-sm">
              <span className="truncate text-slate-300">{file?.name || "No file selected"}</span>
              {file && <span className="text-slate-500">{Math.max(1, Math.round(file.size / 1024))} KB</span>}
            </div>
          </div>
          <p className="mt-4 text-xs text-slate-400">
            Drag to rotate and scroll to zoom. Final orientation and supports are reviewed before production.
          </p>
        </div>

        <div className="bg-slate-50 p-6 text-slate-900 dark:bg-slate-800 dark:text-white md:p-10">
          <h2 className="text-2xl font-bold">2. Configure your print</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <label className="text-sm font-semibold">
              Material
              <select className={fieldClass} value={form.material} onChange={(e) => update("material", e.target.value)}>
                {materialList.map((material) => (
                  <option key={material.id} value={material.id} disabled={material.is_available === false}>
                    {material.name}{material.is_available === false ? " · Coming soon" : ""}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs font-normal text-slate-500 dark:text-slate-300">
                {materialList.find((item) => item.id === form.material)?.description}
              </span>
            </label>

            <label className="text-sm font-semibold">
              Colour
              <select className={fieldClass} value={form.color_id} onChange={(e) => update("color_id", e.target.value)}>
                <option value="">Best available / confirm later</option>
                {colors.map((color) => (
                  <option key={color.id} value={color.id}>{color.name}</option>
                ))}
              </select>
              {selectedColor?.hex_code && (
                <span className="mt-2 flex items-center gap-2 text-xs font-normal text-slate-500 dark:text-slate-300">
                  <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: selectedColor.hex_code }} />
                  Preview updated
                </span>
              )}
            </label>

            <label className="text-sm font-semibold">
              Surface quality
              <select className={fieldClass} value={form.quality} onChange={(e) => update("quality", e.target.value)}>
                {qualityList.map((quality) => (
                  <option key={quality.id} value={quality.id}>{quality.name}</option>
                ))}
              </select>
            </label>

            <label className="text-sm font-semibold">
              Infill
              <select className={fieldClass} value={form.infill_percent} onChange={(e) => update("infill_percent", e.target.value)}>
                <option value="10">10% · display model</option>
                <option value="20">20% · standard</option>
                <option value="40">40% · strong</option>
                <option value="60">60% · heavy duty</option>
                <option value="100">100% · solid</option>
              </select>
            </label>

            <label className="text-sm font-semibold">
              Quantity
              <input className={fieldClass} type="number" min="1" max="100" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} />
            </label>

            <label className="text-sm font-semibold">
              Model preparation
              <select className={fieldClass} value={form.design_hours} onChange={(e) => update("design_hours", e.target.value)}>
                <option value="0">Print-ready STL · A$0</option>
                <option value="0.25">Minor mesh repair · 15 min</option>
                <option value="0.5">Modification help · 30 min</option>
                <option value="1">CAD/design help · 1 hour</option>
              </select>
            </label>

            <label className="text-sm font-semibold">
              Finishing per part
              <select className={fieldClass} value={form.finishing_hours} onChange={(e) => update("finishing_hours", e.target.value)}>
                <option value="0">Supports removed · included</option>
                <option value="0.1667">Clean and deburr · 10 min</option>
                <option value="0.5">Sand and detail · 30 min</option>
                <option value="1">Presentation finish · 1 hour</option>
              </select>
            </label>

            <label className="text-sm font-semibold">
              Email (optional)
              <input className={fieldClass} type="email" placeholder="you@example.com" value={form.email} onChange={(e) => update("email", e.target.value)} />
            </label>
          </div>

          <label className="mt-5 block text-sm font-semibold">
            Job notes
            <textarea className={fieldClass} rows="3" placeholder="Fit, load, temperature, deadline or other requirements…" value={form.notes} onChange={(e) => update("notes", e.target.value)} />
          </label>

          <label className="mt-5 flex items-center gap-3 rounded-xl border border-slate-300 p-4 dark:border-slate-600">
            <input type="checkbox" checked={form.express} onChange={(e) => update("express", e.target.checked)} className="h-5 w-5 accent-cyan-500" />
            <span>
              <strong>Express production</strong>
              <span className="block text-xs text-slate-500 dark:text-slate-300">Priority queue · +25%</span>
            </span>
          </label>

          {error && (
            <div className="mt-5 rounded-lg bg-red-100 px-4 py-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={getQuote}
            disabled={!file || !csrfToken || uploading}
            className="mt-6 w-full rounded-xl bg-cyan-400 px-6 py-4 text-lg font-black text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {uploading ? "Analysing your model…" : "Get instant estimate"}
          </button>

          {quote && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-600 dark:bg-slate-900">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-cyan-600">Estimate #{quote.id}</p>
                  <h3 className="mt-1 text-3xl font-black">A${(quote.total_price_cents / 100).toFixed(2)}</h3>
                  {quote.quantity > 1 && <p className="text-sm text-slate-500">A${(quote.price_cents / 100).toFixed(2)} per part</p>}
                </div>
                <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-bold text-cyan-800">AUD estimate</span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 text-sm dark:bg-slate-800">
                <div><span className="block text-xs text-slate-500">Size</span>{quote.bbox_mm.map((value) => value.toFixed(1)).join(" × ")} mm</div>
                <div><span className="block text-xs text-slate-500">Estimated weight</span>{quote.estimated_weight_g.toFixed(1)}g each</div>
                <div><span className="block text-xs text-slate-500">Print time</span>≈ {quote.estimated_machine_hours.toFixed(1)} hr each</div>
                <div><span className="block text-xs text-slate-500">Specification</span>{quote.material} · {quote.layer_height_mm}mm · {quote.infill_percent}%</div>
              </div>

              <div className="mt-4 space-y-2 border-y border-slate-200 py-4 text-sm dark:border-slate-700">
                {[
                  ["Setup", quote.breakdown.setup],
                  ["Material", quote.breakdown.material],
                  ["Machine time", quote.breakdown.machine],
                  ["CAD/design", quote.breakdown.design],
                  ["Finishing", quote.breakdown.finishing],
                  ["Express", quote.breakdown.express],
                ].filter(([, value]) => value > 0).map(([label, value]) => (
                  <div key={label} className="flex justify-between"><span className="text-slate-500 dark:text-slate-300">{label}</span><span>A${value.toFixed(2)}</span></div>
                ))}
                {quote.breakdown.bulk_discount > 0 && (
                  <div className="flex justify-between text-green-600"><span>Quantity discount ({quote.breakdown.bulk_discount_percent}%)</span><span>−A${quote.breakdown.bulk_discount.toFixed(2)}</span></div>
                )}
                {quote.breakdown.minimum_applied && <p className="pt-2 text-xs text-slate-500">A$20 minimum job charge applied.</p>}
              </div>

              <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                Geometry-based estimate. Orientation, supports and manufacturability are reviewed before production.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <button type="button" onClick={addToCart} disabled={addingToCart} className="rounded-lg bg-slate-900 px-5 py-3 font-bold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-cyan-400 dark:text-slate-950">
                  {addingToCart ? "Adding…" : "Add quote to cart"}
                </button>
                <Link to="/cart" className="font-bold text-cyan-600 hover:underline">View cart</Link>
              </div>
              {cartMessage && <p className="mt-4 text-sm font-semibold text-green-600">{cartMessage}</p>}
            </div>
          )}
        </div>
      </section>

      <section className="grid gap-6 bg-slate-950 px-6 py-10 sm:grid-cols-2 md:grid-cols-5 md:px-10">
        {[
          ["A$20", "Minimum job"],
          ["A$10", "Setup"],
          ["A$6/hr", "Machine time"],
          ["A$80/hr", "CAD/design"],
          ["A$60/hr", "Finishing"],
        ].map(([price, label]) => (
          <div key={label} className="rounded-xl border border-white/10 p-5">
            <p className="text-2xl font-black text-cyan-400">{price}</p>
            <p className="mt-1 text-sm text-slate-400">{label}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
